/**
 * Heading-anchor slugs — the single source of truth shared by the markdown
 * renderer (which stamps heading `id`s) and the article TOC derivation (which
 * emits the matching anchor hrefs). Because both call the same `Slugger` over
 * the same document in the same order, the rendered ids and the on-this-page
 * anchors agree by construction — no rehype-slug dependency, no cosmetic TOC.
 */

/** GitHub-style slug: lowercase, drop backticks, non-alphanumerics → hyphen. */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/`/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Stateful slugger that de-duplicates within ONE document pass, in render
 * order: a second "Overview" becomes "overview-1". Use a fresh instance per
 * document (one per markdown render, one per `deriveToc` call) so the renderer
 * and the TOC produce identical anchors.
 */
export class Slugger {
  private seen = new Map<string, number>();

  slug(text: string): string {
    const base = slugify(text) || "section";
    const n = this.seen.get(base) ?? 0;
    this.seen.set(base, n + 1);
    return n === 0 ? base : `${base}-${n}`;
  }
}
